import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@heroui/react';
import type { Account } from '../api/client';
import { FeatherIcon } from './FeatherIcon';

/** Kill switch (ADR 0042, CONTEXT.md): an emergency stop on every automation for one
    Account. Humans keep working its chats in the CRM. Both directions are confirmed. */
export function KillSwitchCard({
  account,
  canEdit,
  setKillSwitch,
  onChange,
}: {
  account: Account;
  canEdit: boolean;
  setKillSwitch: (accountId: string, on: boolean, reason?: string) => Promise<Account>;
  onChange: (updated: Account) => void;
}) {
  const { t } = useTranslation('dashboard');
  const on = !!account.kill_switch;
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const updated = await setKillSwitch(account.account_id, !on, on ? undefined : reason.trim() || undefined);
      onChange(updated);
      setConfirming(false);
      setReason('');
    } catch (err) {
      alert(err instanceof Error ? err.message : t('accountSettings.killSwitch.failed'));
    } finally {
      setSaving(false);
    }
  };

  const since = account.kill_switch_on_at ? new Date(account.kill_switch_on_at).toLocaleString() : null;

  return (
    <Card className="bg-[var(--color-bg-card)] border border-red-500/30">
      <CardHeader>
        <CardTitle className="text-red-400 flex items-center gap-2">
          <FeatherIcon name="octagon" size={18} />
          {t('accountSettings.killSwitch.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {on ? (
          <div className="space-y-1 text-sm">
            <p className="font-medium text-red-400">{t('accountSettings.killSwitch.onState')}</p>
            {since && <p className="text-[var(--color-text-secondary)]">{t('accountSettings.killSwitch.since', { when: since })}</p>}
            {account.kill_switch_reason && (
              <p className="text-[var(--color-text-secondary)]">{t('accountSettings.killSwitch.reasonShown', { reason: account.kill_switch_reason })}</p>
            )}
          </div>
        ) : (
          <p className="text-[var(--color-text-secondary)] text-sm">{t('accountSettings.killSwitch.offHint')}</p>
        )}

        {canEdit && !confirming && (
          <Button variant={on ? 'outline' : 'danger'} onPress={() => setConfirming(true)}>
            <FeatherIcon name={on ? 'play-circle' : 'octagon'} size={16} />
            {on ? t('accountSettings.killSwitch.turnOff') : t('accountSettings.killSwitch.turnOn')}
          </Button>
        )}

        {canEdit && confirming && (
          <div className="space-y-3 rounded-lg border border-red-500/30 p-3">
            <p className="text-sm text-[var(--color-text-primary)]">
              {on ? t('accountSettings.killSwitch.confirmOff') : t('accountSettings.killSwitch.confirmOn')}
            </p>
            {!on && (
              <label className="block text-sm text-[var(--color-text-secondary)]">
                {t('accountSettings.killSwitch.reasonLabel')}
                <input
                  value={reason}
                  maxLength={500}
                  onChange={(e) => setReason(e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-lg bg-[var(--color-bg-input)] border border-[var(--color-border-default)] text-[var(--color-text-primary)] focus:outline-none focus:border-red-400"
                />
              </label>
            )}
            <div className="flex gap-2">
              <Button variant={on ? 'primary' : 'danger'} onPress={() => void submit()} isDisabled={saving}>
                {t('accountSettings.killSwitch.confirm')}
              </Button>
              <Button variant="ghost" onPress={() => { setConfirming(false); setReason(''); }} isDisabled={saving}>
                {t('common:cancel')}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
